import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';

import FieldActionButtons from './FieldActionButtons';

export enum ColumnWidth {
  SMALL = 'columnWidth-small',
  DEFAULT = 'columnWidth-medium',
  LARGE = 'columnWidth-large',
}

function DeleteRowWhenNotEditingConfirmationModal(props: {
  modalBody?: JSX.Element;
  onRequestSubmit: () => void;
  onRequestClose: () => void;
}): JSX.Element {
  return (
    <Modal
      title="Confirm Delete"
      onRequestSubmit={props.onRequestSubmit}
      onRequestClose={props.onRequestClose}
      scrollable={true}
      medium={true}
      className="modal-text-only"
      buttons={[
        {text: 'Cancel', cancel: true},
        {text: 'Delete', submit: true, style: ButtonStyle.NEGATIVE},
      ]}
    >
      {props.modalBody || <>Are you sure you want to delete this row? This action is irreversible.</>}
    </Modal>
  );
}

export interface MetadataTableColumnDefinition<T> {
  columnHeader: JSX.Element;
  columnWidth?: ColumnWidth;
  mapItemToColumnContent: (item: T, rowIndex: number) => JSX.Element;
}

export interface MetadataTableContentProps<T> {
  columnDefinitions: Array<MetadataTableColumnDefinition<T>>;
  items: Array<T | null>;
  // Don't just compute this as items.length === 0 because some parent components render additional items in
  // the table via a custom component
  isEmpty: boolean;
  emptyMessage: string;
  isEditing: boolean;
  canRemoveRows: boolean;
  handleRemoveRow: (rowIndex: number) => void;
  confirmDeleteModalBody?: JSX.Element;
  isRoomy?: boolean;
  isLoading: boolean;
}

function LoadingStateContents<T>(props: {
  columnDefinitions: Array<MetadataTableColumnDefinition<T>>;
  isRoomy?: boolean;
}): JSX.Element {
  return (
    <div className="simpleDivTable-row">
      {props.columnDefinitions.map((columnDefinition, columnIndex) => (
        <div className={css(styles[columnDefinition.columnWidth || ColumnWidth.DEFAULT])} key={columnIndex}>
          <div className="metadata-field-row">
            <div
              className={classNames(
                'metadata-field-col',
                css(props.isRoomy ? styles.extraLineHeight : styles.regularLineHeight)
              )}
            >
              {columnIndex === 0 ? <HorizontalThrobber /> : ''}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MetadataTableContent<T>(props: MetadataTableContentProps<T>): JSX.Element {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const maybeRenderRemoveButton = (columnIndex: number, rowIndex: number) =>
    columnIndex === props.columnDefinitions.length - 1 &&
    props.canRemoveRows && (
      <FieldActionButtons
        className={classNames(
          'metadata-field-removeButton',
          css(props.isRoomy ? styles.extraLineHeight : styles.regularLineHeight)
        )}
        isEditing={false} // always show the delete button (i.e. not cancel/complete)
        remove={async () => {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          const handleDelete = () => {
            props.handleRemoveRow(rowIndex);
          };
          if (props.isEditing) {
            handleDelete();
          } else {
            await runModal(DeleteRowWhenNotEditingConfirmationModal, {
              modalBody: props.confirmDeleteModalBody,
              onRequestSubmit: handleDelete,
            });
          }
        }}
      />
    );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const renderRows = () =>
    props.items.map(
      (item, rowIndex) =>
        item && (
          <div
            className="simpleDivTable-row metadata-field"
            key={rowIndex}
            {...getTestProps({component: 'MetadataTableContent', element: 'row'})}
          >
            {props.columnDefinitions.map((columnDefinition, columnIndex) => (
              <div
                className={css(styles[columnDefinition.columnWidth || ColumnWidth.DEFAULT])}
                key={columnIndex}
              >
                <div className="metadata-field-row">
                  <div
                    className={classNames(
                      'metadata-field-col',
                      css(props.isRoomy ? styles.extraLineHeight : styles.regularLineHeight)
                    )}
                    {...getTestProps({component: 'MetadataTableContent', element: 'column'})}
                  >
                    {columnDefinition.mapItemToColumnContent(item, rowIndex)}
                  </div>
                  {maybeRenderRemoveButton(columnIndex, rowIndex)}
                </div>
              </div>
            ))}
          </div>
        )
    );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const renderEmptyMessage = () => (
    <div className="simpleDivTable-row">
      <div className="simpleDivTable-cell simpleDivTable-cell--emptyMessage">{props.emptyMessage}</div>
    </div>
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const renderTableContents = () =>
    props.isLoading ? (
      <LoadingStateContents {...props} />
    ) : props.isEmpty ? (
      renderEmptyMessage()
    ) : (
      renderRows()
    );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const renderColumnHeaders = () => (
    <div className="simpleDivTable-header">
      {props.columnDefinitions.map((columnDefinition, columnIndex) => {
        const columnWidth = columnDefinition.columnWidth || ColumnWidth.DEFAULT;
        return (
          <div className={css(styles[columnWidth])} key={columnIndex}>
            {columnDefinition.columnHeader}
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      {renderColumnHeaders()}
      {renderTableContents()}
    </div>
  );
}

const styles = StyleSheet.create({
  [ColumnWidth.SMALL]: {
    flex: '2',
  },
  [ColumnWidth.DEFAULT]: {
    flex: '3',
  },
  [ColumnWidth.LARGE]: {
    flex: '5',
  },
  regularLineHeight: {
    lineHeight: '16px',
  },
  extraLineHeight: {
    lineHeight: '28px',
  },
});
